/**
 * Pure form-field computation helpers extracted from TraversalStore.
 *
 * Each function is side-effect-free — it receives current Data and returns new
 * Data plus the patches needed for the API, making them individually testable
 * without MobX context.
 *
 * The MobX actions in TraversalStore remain thin orchestrators:
 *   1. Call a helper here to get the new state + patches
 *   2. Assign to this.data (observable)
 *   3. Propagate to Final Form (this.form.change)
 *   4. Dispatch to API (this.patch)
 */
import { pick } from 'dot-object'
import { toJS } from 'mobx'
import { buildObjFromPath } from 'utils/objectPath'
import { generateSetPatch, getSafeValue } from 'utils/patch'
import { Data, Patch } from 'interfaces/ApolloResponse.interface'
import { FieldChange } from './types'

// ---------------------------------------------------------------------------
// Single-field change
// ---------------------------------------------------------------------------

export interface SingleFieldResult {
  /** New data with the path applied */
  data: Data
  /** API patch to send upstream */
  patch: Patch
  /** Processed value (safe, ready for Final Form) */
  value: unknown
}

/**
 * Applies a single path→value change to `currentData` and returns the updated
 * data object together with the corresponding API patch and the processed value.
 * Does NOT mutate `currentData`.
 */
export const applyFieldChange = <T>(
  path: string,
  fieldValue: T | T[] | undefined,
  currentData: Data,
  merge?: boolean
): SingleFieldResult => {
  const value = getSafeValue(fieldValue)
  const data = buildObjFromPath<Data>(path, value, toJS(currentData), merge)
  const patch = generateSetPatch(path, value)
  return { data, patch, value }
}

// ---------------------------------------------------------------------------
// Multiple-field change
// ---------------------------------------------------------------------------

export interface MultipleFieldsResult {
  /** New data with all paths applied */
  data: Data
  /** API patches to send upstream (one per field) */
  patches: Patch[]
  /** Processed values keyed by path (for Final Form propagation) */
  valuesByPath: Record<string, unknown>
}

/**
 * Applies multiple path→value changes sequentially to `currentData`.
 * Returns the final data, all patches, and processed values.
 * Does NOT mutate `currentData`.
 */
export const applyMultipleFieldChanges = <T>(
  payload: FieldChange<T>[],
  currentData: Data
): MultipleFieldsResult => {
  let data = toJS(currentData)
  const patches: Patch[] = []
  const valuesByPath: Record<string, unknown> = {}

  payload.forEach(({ path, value: original, merge }) => {
    const value = getSafeValue(original)
    data = buildObjFromPath<Data>(path, value, data, merge)
    patches.push(generateSetPatch(path, value))
    valuesByPath[path] = value
  })

  return { data, patches, valuesByPath }
}

// ---------------------------------------------------------------------------
// Path-list helpers
// ---------------------------------------------------------------------------

/**
 * Returns the item at `idx` in the list stored at `path` in `data`.
 */
export const pickListItem = <T = string>(
  path: string,
  idx: number,
  data: Data
): T | undefined => {
  const list: T[] = pick(path, data) || []
  return list[idx]
}

/**
 * Returns a new array with `item` appended to the existing list found at
 * `path`, capped to `counterPath` length. Does NOT mutate `data`.
 */
export const buildListWithAddedItem = <P>(
  path: string,
  item: P,
  counterPath: string,
  data: Data
): P[] => {
  const itemCount: number = pick(counterPath, data) || 0
  return [...((pick(path, data) as P[]) || []).slice(0, itemCount), item]
}

/**
 * Returns a new array with the last item removed from the list at `path`,
 * capped to `counterPath` length. Does NOT mutate `data`.
 */
export const buildListWithRemovedItem = <P>(
  path: string,
  counterPath: string,
  data: Data
): P[] => {
  const itemCount: number = pick(counterPath, data) || 0
  const items: P[] = ((pick(path, data) as P[]) || []).slice(0, itemCount)
  items.splice(-1, 1)
  return items
}
